import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { apdmPercent } from '@euler/functions/apdm/campaignFunnel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignReport, ApdmReportBreakdown } from '@euler/model/apdm';
import dayjs from 'dayjs';
import { memo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { ApdmCard, ApdmSectionTitle } from '../components/ApdmSurface';
import { CampaignSummaryCard } from './CampaignSummaryCard';

const kSourceLabels: Record<string, string> = {
  platform: '门店客户',
  import: '导入的客户表格',
  listed: '指定的客户',
};

function Table({
  head,
  rows,
}: {
  head: string[];
  rows: Array<{ key: string; cells: string[] }>;
}) {
  const { rpx } = useResponsiveContext();
  const cell = (text: string, index: number, header: boolean) => (
    <Typography.Text
      key={index}
      size={rpx(12)}
      {...(header ? { medium: true as const } : {})}
      color={header ? aiColors.mutedForeground : aiColors.foreground}
      numberOfLines={1}
      style={{
        flex: index === 0 ? 1.6 : 1,
        textAlign: index === 0 ? 'left' : 'right',
      }}
    >
      {text}
    </Typography.Text>
  );
  return (
    <View>
      <View
        style={{
          flexDirection: 'row',
          paddingBottom: rpx(8),
          borderBottomWidth: 1,
          borderBottomColor: aiColors.border,
        }}
      >
        {head.map((text, index) => cell(text, index, true))}
      </View>
      {rows.map(row => (
        <View
          key={row.key}
          style={{
            flexDirection: 'row',
            paddingVertical: rpx(8),
            borderBottomWidth: 1,
            borderBottomColor: aiColors.border,
          }}
        >
          {row.cells.map((text, index) => cell(text, index, false))}
        </View>
      ))}
    </View>
  );
}

function metricsCells(row: ApdmReportBreakdown): string[] {
  const { metrics } = row;
  return [
    String(metrics.reached),
    String(metrics.replied),
    apdmPercent(metrics.replied, metrics.reached),
    String(metrics.converted),
  ];
}

const kMetricsHead = ['已触达', '回复', '回复率', '转化'];

/** Where the replies and results came from, and the AI's summary. */
export const AnalysisTab = memo(
  ({
    campaignId,
    report,
    ended,
  }: {
    campaignId: string;
    report: ApdmCampaignReport | null;
    /** An ended campaign's summary was written at the end; show it. */
    ended: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    if (!report) return <ActivityIndicator color={aiColors.primary} />;
    const themesTotal = report.replyThemes.reduce(
      (sum, theme) => sum + theme.count,
      0,
    );
    return (
      <View style={{ gap: rpx(12) }}>
        <CampaignSummaryCard campaignId={campaignId} ended={ended} />
        {report.replyThemes.length ? (
          <ApdmCard style={{ padding: rpx(16), gap: rpx(10) }}>
            <ApdmSectionTitle title="客户都回了什么" />
            {report.replyThemes.map(theme => (
              <View key={theme.intent} style={{ gap: rpx(4) }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography.Text size={rpx(12)} color={aiColors.foreground}>
                    {theme.label}
                  </Typography.Text>
                  <Typography.Text
                    size={rpx(12)}
                    color={aiColors.mutedForeground}
                  >
                    {theme.count}
                  </Typography.Text>
                </View>
                <View
                  style={{
                    height: rpx(6),
                    borderRadius: rpx(3),
                    backgroundColor: aiColors.muted,
                    overflow: 'hidden',
                  }}
                >
                  <View
                    style={{
                      width: `${(theme.count / themesTotal) * 100}%`,
                      height: '100%',
                      backgroundColor: aiColors.violet,
                    }}
                  />
                </View>
              </View>
            ))}
          </ApdmCard>
        ) : null}
        <ApdmCard style={{ padding: rpx(16), gap: rpx(10) }}>
          <ApdmSectionTitle title="按顾问" />
          <Table
            head={['顾问', '回复', '审核', '平均用时', '转化']}
            rows={report.advisors.map(row => ({
              key: row.key,
              cells: [
                row.label,
                String(row.metrics.replied),
                String(row.draftsReviewed),
                row.averageReviewSeconds == null
                  ? '—'
                  : `${Math.max(
                      1,
                      Math.round(row.averageReviewSeconds / 60),
                    )} 分钟`,
                String(row.metrics.converted),
              ],
            }))}
          />
        </ApdmCard>
        <ApdmCard style={{ padding: rpx(16), gap: rpx(10) }}>
          <ApdmSectionTitle title="按日期" note="客户加入活动的日期" />
          <Table
            head={['日期', ...kMetricsHead]}
            rows={report.days.map(row => ({
              key: row.key,
              cells: [dayjs(row.key).format('M月D日'), ...metricsCells(row)],
            }))}
          />
        </ApdmCard>
        <ApdmCard style={{ padding: rpx(16), gap: rpx(10) }}>
          <ApdmSectionTitle title="按客户来源" />
          <Table
            head={['来源', ...kMetricsHead]}
            rows={report.sources.map(row => ({
              key: row.key,
              cells: [
                kSourceLabels[row.key] ?? row.label,
                ...metricsCells(row),
              ],
            }))}
          />
        </ApdmCard>
      </View>
    );
  },
);
